import { useEffect } from 'react';

import { getAppStore } from '@/store';
import { Screen } from '@/types';
import sendCreditsInfoAnalytics from '@/utils/analytics/sendCreditsInfoAnalytics';
import trackPresetCredits from '@/utils/analytics/trackPresetCredits';
import watchCreditsBalance from '@/utils/analytics/watchCreditsBalance';
import useAuth from './useAuth';

/**
 * Auth state the last `credits_info` balance was sent for. Module-level, so a
 * remount (StrictMode included) doesn't resend `app_open`.
 */
let reportedAuth: boolean | undefined;

/**
 * Session-level `credits_info`: the balance once per open (`app_open`; `null`
 * for a guest), and again after an in-app sign-in (`login`). A sign-in on the
 * Create screen also re-reports the open preset, whose price only becomes
 * quotable once signed in. Both wait for the balance the credits subscription
 * delivers — no request of their own.
 */
const useCreditsInfoAnalytics = () => {
  const { isAuthorized } = useAuth();

  useEffect(() => {
    if (reportedAuth === isAuthorized) return undefined;
    const trigger = reportedAuth === undefined ? 'app_open' : 'login';

    if (!isAuthorized) {
      if (trigger === 'app_open') {
        sendCreditsInfoAnalytics({
          settings: { trigger, credits_balance: null },
        });
      }
      reportedAuth = false;
      return undefined;
    }

    if (trigger === 'login') {
      const { screen, selectedPreset, activeTabSlug } = getAppStore();
      if (screen === Screen.Create && selectedPreset?.slug) {
        trackPresetCredits({
          slug: selectedPreset.slug,
          category: activeTabSlug ?? '',
          trigger,
        });
      }
    }

    return watchCreditsBalance((balance) => {
      reportedAuth = true;
      sendCreditsInfoAnalytics({
        settings: { trigger, credits_balance: balance },
      });
    });
  }, [isAuthorized]);
};

export default useCreditsInfoAnalytics;
